<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-50">
      <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
        leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/70 backdrop-blur-md" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95">
            <DialogPanel
              class="w-full max-w-5xl transform overflow-hidden rounded-3xl bg-nera-elevated-1 border border-nera-light-border p-6! text-left align-middle shadow-2xl transition-all">
              <DialogTitle as="h3" class="text-3xl! font-bold! text-nera-text-light mb-6! text-center tracking-wide">
                Choose Your Plan
              </DialogTitle>

              <RadioGroup v-model="selectedPlan">
                <RadioGroupLabel class="sr-only">Upgrade Plan Selection</RadioGroupLabel>

                <div class="flex flex-wrap justify-center gap-4">
                  <RadioGroupOption as="template" v-for="plan in plans" :key="plan.name" :value="plan"
                    :disabled="isComingSoon(plan)" v-slot="{ active, checked }">
                    <div :class="[
                      active ? 'ring-1 ring-nera-accent' : '',
                      checked
                        ? 'border-nera-accent bg-nera-elevated-4 shadow-[0_0_20px_rgba(59,130,246,0.15)]'
                        : 'border-nera-light-border bg-nera-elevated-2 hover:border-nera-light-border-strong',
                    ]"
                      class="relative flex cursor-pointer flex-col w-full max-w-sm flex-1 min-w-[260px] min-h-[360px] rounded-2xl border-[0.5px] p-8 text-center transition-all duration-300">
                      <span v-if="hasDiscount(plan) && !isComingSoon(plan)"
                        class="absolute top-3 right-3 inline-flex items-center rounded-full bg-emerald-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 border border-emerald-300">
                        Save {{ formatDiscount(plan.discount_percentage) }}%
                      </span>

                      <RadioGroupLabel as="h4"
                        class="text-sm font-medium text-nera-muted-light uppercase tracking-widest mb-4">
                        {{ plan.name }}
                      </RadioGroupLabel>

                      <div v-if="!isComingSoon(plan)" class="mb-4 min-h-[88px] flex items-center justify-center">
                        <div v-if="hasDiscount(plan)" class="flex flex-col items-center gap-1">
                          <span class="text-lg text-nera-muted-light line-through">
                            {{ formatPrice(plan.price) }}
                          </span>
                          <span class="text-5xl font-bold text-nera-text-light leading-none">
                            {{ formatPrice(plan.discounted_price) }}
                          </span>
                        </div>
                        <div v-else class="text-5xl font-bold text-nera-text-light leading-none">
                          {{ formatPrice(plan.price) }}
                        </div>
                      </div>
                      <div v-else class="min-h-[88px] flex items-center justify-center mb-4">
                        <span
                          class="inline-flex items-center justify-center text-md uppercase tracking-[0.3em] text-nera-light-text-strong border border-nera-light-border-strong rounded-full py-2.5 px-4">
                          Coming Soon
                        </span>
                      </div>
                      <p class="text-md! text-nera-light-text-strong mb-4">
                        {{ plan.description }}
                      </p>
                      <RadioGroupDescription as="ul" class="flex flex-col gap-2 mb-4 text-xs text-nera-muted-light">
                        <template v-for="(value, key) in plan.features" :key="key">
                          <li v-if="value === true" class="flex items-start gap-1.5 px-2 text-left">
                            <span class="w-1.5 h-1.5 rounded-full bg-nera-muted-light shrink-0 mt-1.5"></span>

                            <span class="leading-snug">
                              {{ key }}
                            </span>
                          </li>
                        </template>
                      </RadioGroupDescription>

                      <div v-if="!isComingSoon(plan)" class="mt-auto pt-4">
                        <div :class="checked
                          ? 'bg-nera-accent text-nera-text-muted-light border-transparent'
                          : 'bg-transparent text-nera-light-text-strong border-nera-light-border-strong hover:border-nera-light-border-strong'
                          "
                          class="w-full rounded-xl border-[0.5px] py-3 text-xs font-bold uppercase tracking-wider transition-colors">
                          {{ checked ? "Selected" : "Select Plan" }}
                        </div>
                      </div>
                    </div>
                  </RadioGroupOption>
                </div>
              </RadioGroup>

              <div class="mt-6! flex justify-center gap-4 max-w-md mx-auto">
                <button type="button"
                  class="flex-1 inline-flex justify-center rounded-xl border-[0.5px]! border-nera-light-border-strong! bg-transparent! p-3 text-sm font-medium text-nera-light-text-strong hover:bg-nera-light-hover! transition-colors focus:outline-none"
                  @click="closeModal">
                  Cancel
                </button>
                <button type="button"
                  class="flex-1 inline-flex justify-center rounded-xl border-[0.5px]! border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-muted-light hover:bg-nera-accent-hover! transition-colors! focus:outline-none"
                  @click="confirmUpgrade">
                  Confirm Upgrade
                </button>
              </div>
              <div class="mt-5! flex items-center justify-center gap-1 text-xs text-nera-muted-light">
                <span>Payments secured & powered by</span>
                <img :src="stripeWordmark" alt="Stripe" style="height: 16px; width: auto;" class="translate-y-1px" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot appear :show="isWarningOpen" as="template">
    <Dialog as="div" @close="cancelWarning" class="relative z-[60]">
      <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
        leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/80 backdrop-blur-md" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95">
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-3xl bg-nera-elevated-1 border border-nera-light-border p-6! text-left align-middle shadow-2xl transition-all">
              <DialogTitle as="h3" class="text-xl! font-bold! text-nera-text-light mb-4! text-center tracking-wide">
                Confirm Plan Change
              </DialogTitle>
              <p class="text-sm text-nera-light-text-strong mb-6! text-center leading-relaxed">
                Switching plans will end your current subscription immediately. Any remaining benefits and unused
                subscription credits from your current plan will be lost and cannot be recovered.
              </p>
              <div class="flex justify-center gap-3">
                <button type="button"
                  class="flex-1 inline-flex justify-center rounded-xl border-[0.5px]! border-nera-light-border-strong! bg-transparent! p-3 text-sm font-medium text-nera-light-text-strong hover:bg-nera-light-hover! transition-colors focus:outline-none"
                  @click="cancelWarning">
                  Go Back
                </button>
                <button type="button"
                  class="flex-1 inline-flex justify-center rounded-xl border-[0.5px]! border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-muted-light hover:bg-nera-accent-hover! transition-colors! focus:outline-none"
                  @click="proceedWithUpgrade">
                  Continue
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, onMounted } from "vue";
import stripeWordmark from "../images/Stripe wordmark - Blurple - Small.png";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  RadioGroup,
  RadioGroupLabel,
  RadioGroupDescription,
  RadioGroupOption,
} from "@headlessui/vue";

// Modal State
const isOpen = ref(false);
const isWarningOpen = ref(false);
const plans = ref([]);

onMounted(() => {
  fetchPlans();
});

const openModal = () => {
  isOpen.value = true;
};

const closeModal = () => {
  isOpen.value = false;
};

defineExpose({
  openModal,
  closeModal,
});

const formatPrice = (price) => {
  if (price === 0) return 'Free'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)
}

const isComingSoon = (plan) => typeof plan.position === 'number' && plan.position < 0

const hasDiscount = (plan) => Number(plan.discount_percentage) > 0

const formatDiscount = (pct) => {
  const n = Number(pct)
  return Number.isInteger(n) ? n.toString() : n.toString().replace(/\.?0+$/, '')
}

const fetchPlans = async () => {
  try {
    const response = await fetch("/api/paid_plans");
    if (!response.ok) {
      throw new Error("Failed to fetch plans");
    }
    const data = await response.json();
    plans.value = data;
  } catch (error) {
    console.error("Error fetching plans:", error);
  }
};

const selectedPlan = ref(plans.value[1]); // Default to Pro plan

const confirmUpgrade = () => {
  if (!selectedPlan.value) return;
  isWarningOpen.value = true;
};

const cancelWarning = () => {
  isWarningOpen.value = false;
};

const proceedWithUpgrade = async () => {
  isWarningOpen.value = false;
  console.log("Upgrading to:", selectedPlan.value.name);
  const response = await fetch("/api/subscriptions/create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token":
        document.querySelector('[name="csrf-token"]')?.content || "",
    },
    body: JSON.stringify({ plan_name: selectedPlan.value.name }),
  });
  const data = await response.json();
  if (response.ok) {
    window.location.href = data.checkout_url;
  } else {
    alert("Failed to create subscription: " + data.error);
  }
  closeModal();
};
</script>
